package ai.wagemix.data

import ai.wagemix.net.models.WMCheckoutSummary
import ai.wagemix.net.models.WMJobDescriptionFile
import ai.wagemix.net.models.WMPlanPrice
import ai.wagemix.net.models.WMOwnedShare
import ai.wagemix.net.models.WMShareStats
import ai.wagemix.net.models.WMShareResult
import ai.wagemix.net.models.WMStatsCollection
import ai.wagemix.net.models.WMStoredMix
import ai.wagemix.net.models.WMUpload
import ai.wagemix.net.models.WMUploadProgress
import ai.wagemix.net.models.firebase.WMSharedMix
import com.fsryan.foundation.data.FSLocalContext
import com.fsryan.foundation.data.FSLocalContextDataSource
import com.fsryan.foundation.data.FSLocalDataSource
import com.fsryan.foundation.data.FSLocalDataUpdate
import com.fsryan.foundation.data.nestedValueWasChanged
import com.fsryan.foundation.data.retrieveNestedValue
import com.fsryan.foundation.data.upsertNestedValue
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.shareIn
import kotlin.jvm.JvmInline

/*
 * The client's in-memory local context (local-context playbooks). Everything here may be lost on
 * reload; what must survive a reload lives in [WMCache].
 *
 * Context-id policy. The context id partitions the bag into scopes:
 *  - process-wide values (stats results, plan prices, checkout and portal URLs, the public shared
 *    mix, in-flight sync guards) live under [WMLocalContextDataSource.GLOBAL_CONTEXT_ID];
 *  - per-user values (the selected mix, upload progress, the occupation picker's state, screen
 *    parameters, file URLs, share results, the mirrored stored mixes, uploads and own shares) live under the
 *    user's uid, so two accounts on one machine never see each other's state. When nobody is
 *    signed in they live under [WMLocalContextDataSource.ANONYMOUS_CONTEXT_ID], which is what
 *    `uid == null` selects.
 * Every per-user operation takes `uid: String?` and derives its context id through
 * [WMLocalContextDataSource.userContextId].
 */

/** The typed snapshot of one context (global or one user). Reads return `null` for "absent". */
interface WMLocalContext {

    // --- process-wide ---
    fun stats(signature: String): WMStatsCollection?
    fun planPrices(): List<WMPlanPrice>?
    fun checkoutSummary(sessionId: String): WMCheckoutSummary?
    /** The Stripe Checkout URL a `startCheckout` produced for [lookupKey]. */
    fun checkoutUrl(lookupKey: String): String?
    /** The Stripe billing-portal URL an `openBillingPortal` produced. */
    fun billingPortalUrl(): String?
    /** The account email a verified password-reset [code] belongs to. */
    fun passwordResetEmail(code: String): String?
    fun sharedMix(sharedMixId: String): WMSharedMix?

    // --- per user ---
    fun selectedMixId(): String?
    fun uploadProgress(eventStreamId: String): WMUploadProgress?
    fun pickerSelection(): Set<Int>
    fun pickerExpansion(): Set<Int>
    /** Small per-screen parameters (a list filter, a query) a presenter must not hold itself. */
    fun screenParams(screen: String): Map<String, String>
    /** The stored job descriptions fetched for the user's mixes, by mix id; their links expire. */
    fun jobDescriptionFiles(): Map<String, WMJobDescriptionFile>
    fun shareResult(mixId: String): WMShareResult?
    /** The user's stored mixes as mirrored from the backend; `null` until the first mirror arrives. */
    fun storedMixes(): List<WMStoredMix>?
    /** The user's uploads as mirrored from the backend; `null` until the first mirror arrives. */
    fun uploads(): List<WMUpload>?
    /** The user's shared mixes as mirrored from the backend, newest first; `null` until the first mirror arrives. */
    fun ownShares(): List<WMOwnedShare>?
    /** Click statistics of the user's share links, mirrored for the Pro plan; `null` on any other plan. */
    fun shareStats(): List<WMShareStats>?

    /** One change to one context. Predicates answer domain questions, never key questions. */
    interface Update: FSLocalDataUpdate<WMLocalContext>, FSLocalDataUpdate.Modification<WMLocalContext> {
        val contextId: String
        override val isSame: Boolean
        override val before: WMLocalContext
        override val after: WMLocalContext

        fun statsChanged(signature: String): Boolean
        fun planPricesChanged(): Boolean
        fun checkoutSummaryChanged(sessionId: String): Boolean
        fun checkoutUrlChanged(lookupKey: String): Boolean
        fun billingPortalUrlChanged(): Boolean
        fun passwordResetEmailChanged(code: String): Boolean
        fun sharedMixChanged(sharedMixId: String): Boolean
        fun selectedMixIdChanged(): Boolean
        fun uploadProgressChanged(eventStreamId: String): Boolean
        fun pickerSelectionChanged(): Boolean
        fun pickerExpansionChanged(): Boolean
        fun screenParamsChanged(screen: String): Boolean
        fun jobDescriptionFilesChanged(): Boolean
        fun shareResultChanged(mixId: String): Boolean
        fun storedMixesChanged(): Boolean
        fun uploadsChanged(): Boolean
        fun ownSharesChanged(): Boolean
        fun shareStatsChanged(): Boolean
    }
}

/**
 * Domain operations over the local context. Observation is through [updateFlow] only, which
 * carries updates of every context id; consumers check [WMLocalContext.Update.contextId].
 */
interface WMLocalContextDataSource: FSLocalDataSource<WMLocalContext, WMLocalContext.Update> {

    suspend fun retrieveGlobal(): WMLocalContext
    suspend fun retrieveUser(uid: String?): WMLocalContext

    // --- process-wide ---
    suspend fun upsertStats(signature: String, stats: WMStatsCollection)
    suspend fun upsertPlanPrices(prices: List<WMPlanPrice>)
    suspend fun upsertCheckoutSummary(sessionId: String, summary: WMCheckoutSummary)
    suspend fun upsertCheckoutUrl(lookupKey: String, url: String)
    suspend fun deleteCheckoutUrl(lookupKey: String)
    suspend fun upsertBillingPortalUrl(url: String)
    suspend fun deleteBillingPortalUrl()
    suspend fun upsertPasswordResetEmail(code: String, email: String)
    suspend fun upsertSharedMix(sharedMixId: String, mix: WMSharedMix)
    suspend fun deleteSharedMix(sharedMixId: String)

    // --- per user ---
    suspend fun upsertSelectedMixId(uid: String?, mixId: String)
    suspend fun deleteSelectedMixId(uid: String?)
    suspend fun upsertUploadProgress(uid: String?, eventStreamId: String, progress: WMUploadProgress)
    suspend fun deleteUploadProgress(uid: String?, eventStreamId: String)
    suspend fun upsertPickerSelection(uid: String?, selection: Set<Int>)
    suspend fun deletePickerSelection(uid: String?)
    suspend fun upsertPickerExpansion(uid: String?, expanded: Set<Int>)
    suspend fun deletePickerExpansion(uid: String?)
    suspend fun upsertScreenParams(uid: String?, screen: String, params: Map<String, String>)
    suspend fun deleteScreenParams(uid: String?, screen: String)
    suspend fun upsertJobDescriptionFiles(uid: String?, files: Map<String, WMJobDescriptionFile>)
    suspend fun upsertShareResult(uid: String?, result: WMShareResult)
    suspend fun deleteShareResult(uid: String?, mixId: String)
    suspend fun upsertStoredMixes(uid: String?, mixes: List<WMStoredMix>)
    suspend fun deleteStoredMixes(uid: String?)
    suspend fun upsertUploads(uid: String?, uploads: List<WMUpload>)
    suspend fun deleteUploads(uid: String?)
    suspend fun upsertOwnShares(uid: String?, shares: List<WMOwnedShare>)
    suspend fun upsertShareStats(uid: String?, stats: List<WMShareStats>?)
    suspend fun deleteOwnShares(uid: String?)
    /** Drops everything the user's context holds (sign-out). */
    suspend fun clearUser(uid: String?)

    // --- in-flight guards (process-wide) ---
    /** Returns `true` for the one caller that won the slot for [operation] on [scope]. */
    suspend fun insertSyncIfAbsent(operation: String, scope: String, sequenceId: String): Boolean
    suspend fun removeSync(operation: String, scope: String)

    companion object {
        const val GLOBAL_CONTEXT_ID = "ai.wagemix.data.GLOBAL"
        const val ANONYMOUS_CONTEXT_ID = "ai.wagemix.data.ANONYMOUS"

        /** The context id of a user's values: the uid, or the anonymous context when nobody is signed in. */
        fun userContextId(uid: String?): String = uid ?: ANONYMOUS_CONTEXT_ID

        private const val ROOT = "ai.wagemix.data"

        /** Every key path starts with the module namespace so another feature sharing a context cannot collide. */
        internal fun key(vararg suffix: String): Array<String> = arrayOf(ROOT, *suffix)

        internal fun statsKey(signature: String) = key("stats", signature)
        internal fun planPricesKey() = key("planPrices")
        internal fun checkoutSummaryKey(sessionId: String) = key("checkoutSummary", sessionId)
        internal fun checkoutUrlKey(lookupKey: String) = key("checkoutUrl", lookupKey)
        internal fun billingPortalUrlKey() = key("billingPortalUrl")
        internal fun passwordResetEmailKey(code: String) = key("passwordResetEmail", code)
        internal fun sharedMixKey(sharedMixId: String) = key("sharedMix", sharedMixId)
        internal fun selectedMixIdKey() = key("selectedMixId")
        internal fun uploadProgressKey(eventStreamId: String) = key("uploadProgress", eventStreamId)
        internal fun pickerSelectionKey() = key("pickerSelection")
        internal fun pickerExpansionKey() = key("pickerExpansion")
        internal fun screenParamsKey(screen: String) = key("screenParams", screen)
        internal fun jobDescriptionFilesKey() = key("jobDescriptionFiles")
        internal fun shareResultKey(mixId: String) = key("shareResult", mixId)
        internal fun storedMixesKey() = key("storedMixes")
        internal fun uploadsKey() = key("uploads")
        internal fun ownSharesKey() = key("ownShares")
        internal fun shareStatsKey() = key("shareStats")
        internal fun syncKey(operation: String, scope: String) = key("sync", operation, scope)
    }
}

/** Wraps the one [FSLocalContextDataSource] of the process (registered by the app) in the typed facade. */
fun WMLocalContextDataSource(localContextDataSource: FSLocalContextDataSource): WMLocalContextDataSource {
    return WMLocalContextDataSourceImpl(wrapped = localContextDataSource)
}

private class WMLocalContextDataSourceImpl(private val wrapped: FSLocalContextDataSource): WMLocalContextDataSource {

    /*
     * Unconfined so the sharing coroutine subscribes to the foundation flow in the same call that
     * registers the first collector; a dispatched start could miss a write made right after subscribing.
     */
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Unconfined)

    /**
     * A typed view of the foundation's shared flow, built on every access. Sharing only while
     * subscribed means an access costs nothing until someone collects and holds nothing afterwards,
     * so there is no state to initialise and no coroutine outliving its collectors.
     */
    override val updateFlow: SharedFlow<WMLocalContext.Update>
        get() = wrapped.updateFlow.map { WMLocalContextUpdateValue(it) }.shareIn(scope, started = SharingStarted.WhileSubscribed())

    private val global = WMLocalContextDataSource.GLOBAL_CONTEXT_ID

    override suspend fun retrieveGlobal(): WMLocalContext = WMLocalContextValue(wrapped.retrieve(global))

    override suspend fun retrieveUser(uid: String?): WMLocalContext {
        return WMLocalContextValue(wrapped.retrieve(WMLocalContextDataSource.userContextId(uid)))
    }

    // --- process-wide ---

    override suspend fun upsertStats(signature: String, stats: WMStatsCollection) {
        wrapped.upsertNestedValue(global, stats, *WMLocalContextDataSource.statsKey(signature))
    }

    override suspend fun upsertPlanPrices(prices: List<WMPlanPrice>) {
        wrapped.upsertNestedValue(global, prices, *WMLocalContextDataSource.planPricesKey())
    }

    override suspend fun upsertCheckoutSummary(sessionId: String, summary: WMCheckoutSummary) {
        wrapped.upsertNestedValue(global, summary, *WMLocalContextDataSource.checkoutSummaryKey(sessionId))
    }

    override suspend fun upsertCheckoutUrl(lookupKey: String, url: String) {
        wrapped.upsertNestedValue(global, url, *WMLocalContextDataSource.checkoutUrlKey(lookupKey))
    }

    override suspend fun deleteCheckoutUrl(lookupKey: String) {
        wrapped.deleteNestedValue(global, *WMLocalContextDataSource.checkoutUrlKey(lookupKey))
    }

    override suspend fun upsertBillingPortalUrl(url: String) {
        wrapped.upsertNestedValue(global, url, *WMLocalContextDataSource.billingPortalUrlKey())
    }

    override suspend fun deleteBillingPortalUrl() {
        wrapped.deleteNestedValue(global, *WMLocalContextDataSource.billingPortalUrlKey())
    }

    override suspend fun upsertPasswordResetEmail(code: String, email: String) {
        wrapped.upsertNestedValue(global, email, *WMLocalContextDataSource.passwordResetEmailKey(code))
    }

    override suspend fun upsertSharedMix(sharedMixId: String, mix: WMSharedMix) {
        wrapped.upsertNestedValue(global, mix, *WMLocalContextDataSource.sharedMixKey(sharedMixId))
    }

    override suspend fun deleteSharedMix(sharedMixId: String) {
        wrapped.deleteNestedValue(global, *WMLocalContextDataSource.sharedMixKey(sharedMixId))
    }

    // --- per user ---

    private fun userId(uid: String?): String = WMLocalContextDataSource.userContextId(uid)

    override suspend fun upsertSelectedMixId(uid: String?, mixId: String) {
        wrapped.upsertNestedValue(userId(uid), mixId, *WMLocalContextDataSource.selectedMixIdKey())
    }

    override suspend fun deleteSelectedMixId(uid: String?) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.selectedMixIdKey())
    }

    override suspend fun upsertUploadProgress(uid: String?, eventStreamId: String, progress: WMUploadProgress) {
        wrapped.upsertNestedValue(userId(uid), progress, *WMLocalContextDataSource.uploadProgressKey(eventStreamId))
    }

    override suspend fun deleteUploadProgress(uid: String?, eventStreamId: String) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.uploadProgressKey(eventStreamId))
    }

    override suspend fun upsertPickerSelection(uid: String?, selection: Set<Int>) {
        wrapped.upsertNestedValue(userId(uid), selection, *WMLocalContextDataSource.pickerSelectionKey())
    }

    override suspend fun deletePickerSelection(uid: String?) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.pickerSelectionKey())
    }

    override suspend fun upsertPickerExpansion(uid: String?, expanded: Set<Int>) {
        wrapped.upsertNestedValue(userId(uid), expanded, *WMLocalContextDataSource.pickerExpansionKey())
    }

    override suspend fun deletePickerExpansion(uid: String?) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.pickerExpansionKey())
    }

    override suspend fun upsertScreenParams(uid: String?, screen: String, params: Map<String, String>) {
        wrapped.upsertNestedValue(userId(uid), params, *WMLocalContextDataSource.screenParamsKey(screen))
    }

    override suspend fun deleteScreenParams(uid: String?, screen: String) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.screenParamsKey(screen))
    }

    override suspend fun upsertJobDescriptionFiles(uid: String?, files: Map<String, WMJobDescriptionFile>) {
        wrapped.upsertNestedValue(userId(uid), files, *WMLocalContextDataSource.jobDescriptionFilesKey())
    }

    override suspend fun upsertShareResult(uid: String?, result: WMShareResult) {
        wrapped.upsertNestedValue(userId(uid), result, *WMLocalContextDataSource.shareResultKey(result.mixId))
    }

    override suspend fun deleteShareResult(uid: String?, mixId: String) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.shareResultKey(mixId))
    }

    override suspend fun upsertStoredMixes(uid: String?, mixes: List<WMStoredMix>) {
        wrapped.upsertNestedValue(userId(uid), mixes, *WMLocalContextDataSource.storedMixesKey())
    }

    override suspend fun deleteStoredMixes(uid: String?) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.storedMixesKey())
    }

    override suspend fun upsertUploads(uid: String?, uploads: List<WMUpload>) {
        wrapped.upsertNestedValue(userId(uid), uploads, *WMLocalContextDataSource.uploadsKey())
    }

    override suspend fun deleteUploads(uid: String?) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.uploadsKey())
    }

    override suspend fun upsertOwnShares(uid: String?, shares: List<WMOwnedShare>) {
        wrapped.upsertNestedValue(userId(uid), shares, *WMLocalContextDataSource.ownSharesKey())
    }

    override suspend fun upsertShareStats(uid: String?, stats: List<WMShareStats>?) {
        if (stats == null) {
            wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.shareStatsKey())
        } else {
            wrapped.upsertNestedValue(userId(uid), stats, *WMLocalContextDataSource.shareStatsKey())
        }
    }

    override suspend fun deleteOwnShares(uid: String?) {
        wrapped.deleteNestedValue(userId(uid), *WMLocalContextDataSource.ownSharesKey())
    }

    override suspend fun clearUser(uid: String?) {
        wrapped.clear(userId(uid))
    }

    // --- in-flight guards ---

    override suspend fun insertSyncIfAbsent(operation: String, scope: String, sequenceId: String): Boolean {
        val keys = WMLocalContextDataSource.syncKey(operation, scope)
        var inserted = false
        wrapped.modifyContext(global) { current ->
            val existing = current.retrieveNestedValue<String>(*keys)
            inserted = existing == null
            if (existing == null) {
                current.upsertNestedValue(sequenceId, *keys)
            } else {
                current
            }
        }
        return inserted
    }

    override suspend fun removeSync(operation: String, scope: String) {
        wrapped.deleteNestedValue(global, *WMLocalContextDataSource.syncKey(operation, scope))
    }
}

/** The snapshot adapter: every read builds its key with the same companion helper the mutation used. */
@JvmInline
internal value class WMLocalContextValue(private val raw: FSLocalContext): WMLocalContext {

    override fun stats(signature: String): WMStatsCollection? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.statsKey(signature))
    }

    override fun planPrices(): List<WMPlanPrice>? = raw.list(WMLocalContextDataSource.planPricesKey())

    override fun checkoutSummary(sessionId: String): WMCheckoutSummary? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.checkoutSummaryKey(sessionId))
    }

    override fun checkoutUrl(lookupKey: String): String? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.checkoutUrlKey(lookupKey))
    }

    override fun billingPortalUrl(): String? = raw.retrieveNestedValue(*WMLocalContextDataSource.billingPortalUrlKey())

    override fun passwordResetEmail(code: String): String? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.passwordResetEmailKey(code))
    }

    override fun sharedMix(sharedMixId: String): WMSharedMix? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.sharedMixKey(sharedMixId))
    }

    override fun selectedMixId(): String? = raw.retrieveNestedValue(*WMLocalContextDataSource.selectedMixIdKey())

    override fun uploadProgress(eventStreamId: String): WMUploadProgress? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.uploadProgressKey(eventStreamId))
    }

    override fun pickerSelection(): Set<Int> = raw.set(WMLocalContextDataSource.pickerSelectionKey())

    override fun pickerExpansion(): Set<Int> = raw.set(WMLocalContextDataSource.pickerExpansionKey())

    override fun screenParams(screen: String): Map<String, String> {
        return raw.map(WMLocalContextDataSource.screenParamsKey(screen))
    }

    override fun jobDescriptionFiles(): Map<String, WMJobDescriptionFile> {
        return raw.map(WMLocalContextDataSource.jobDescriptionFilesKey())
    }

    override fun shareResult(mixId: String): WMShareResult? {
        return raw.retrieveNestedValue(*WMLocalContextDataSource.shareResultKey(mixId))
    }

    override fun storedMixes(): List<WMStoredMix>? = raw.list(WMLocalContextDataSource.storedMixesKey())

    override fun uploads(): List<WMUpload>? = raw.list(WMLocalContextDataSource.uploadsKey())

    override fun ownShares(): List<WMOwnedShare>? = raw.list(WMLocalContextDataSource.ownSharesKey())

    override fun shareStats(): List<WMShareStats>? = raw.list(WMLocalContextDataSource.shareStatsKey())
}

/*
 * The bag stores collections erased; these read them back under the element type the mutation
 * wrote. The data source is the only writer, so the casts are sound.
 */
@Suppress("UNCHECKED_CAST")
private fun <T> FSLocalContext.list(keys: Array<String>): List<T>? = retrieveNestedValue<List<*>>(*keys) as? List<T>

@Suppress("UNCHECKED_CAST")
private fun <T> FSLocalContext.set(keys: Array<String>): Set<T> = (retrieveNestedValue<Set<*>>(*keys) as? Set<T>) ?: emptySet()

@Suppress("UNCHECKED_CAST")
private fun <K, V> FSLocalContext.map(keys: Array<String>): Map<K, V> {
    return (retrieveNestedValue<Map<*, *>>(*keys) as? Map<K, V>) ?: emptyMap()
}

/** The update adapter: predicates use the same companion helpers as the snapshot and the mutations. */
@JvmInline
internal value class WMLocalContextUpdateValue(private val raw: FSLocalContext.Update): WMLocalContext.Update {

    override val contextId: String get() = raw.after.contextId
    override val isSame: Boolean get() = raw.isSame
    override val before: WMLocalContext get() = WMLocalContextValue(raw.before)
    override val after: WMLocalContext get() = WMLocalContextValue(raw.after)

    private fun changed(keys: Array<String>): Boolean = raw.nestedValueWasChanged<Any>(keys = keys)

    override fun statsChanged(signature: String) = changed(WMLocalContextDataSource.statsKey(signature))
    override fun planPricesChanged() = changed(WMLocalContextDataSource.planPricesKey())
    override fun checkoutSummaryChanged(sessionId: String) = changed(WMLocalContextDataSource.checkoutSummaryKey(sessionId))
    override fun checkoutUrlChanged(lookupKey: String) = changed(WMLocalContextDataSource.checkoutUrlKey(lookupKey))
    override fun billingPortalUrlChanged() = changed(WMLocalContextDataSource.billingPortalUrlKey())
    override fun passwordResetEmailChanged(code: String) = changed(WMLocalContextDataSource.passwordResetEmailKey(code))
    override fun sharedMixChanged(sharedMixId: String) = changed(WMLocalContextDataSource.sharedMixKey(sharedMixId))
    override fun selectedMixIdChanged() = changed(WMLocalContextDataSource.selectedMixIdKey())
    override fun uploadProgressChanged(eventStreamId: String) = changed(WMLocalContextDataSource.uploadProgressKey(eventStreamId))
    override fun pickerSelectionChanged() = changed(WMLocalContextDataSource.pickerSelectionKey())
    override fun pickerExpansionChanged() = changed(WMLocalContextDataSource.pickerExpansionKey())
    override fun screenParamsChanged(screen: String) = changed(WMLocalContextDataSource.screenParamsKey(screen))
    override fun jobDescriptionFilesChanged() = changed(WMLocalContextDataSource.jobDescriptionFilesKey())
    override fun shareResultChanged(mixId: String) = changed(WMLocalContextDataSource.shareResultKey(mixId))
    override fun storedMixesChanged() = changed(WMLocalContextDataSource.storedMixesKey())
    override fun uploadsChanged() = changed(WMLocalContextDataSource.uploadsKey())
    override fun ownSharesChanged() = changed(WMLocalContextDataSource.ownSharesKey())
    override fun shareStatsChanged() = changed(WMLocalContextDataSource.shareStatsKey())
}
